import React from "react";

/**
 * Diagonal top-right "✓ VERIFIED" corner ribbon.
 *
 * Design contract (dark-luxe / EuroGirls-inspired but bespoke):
 *  - Positioned absolute, top-right of a `relative` parent (typically the
 *    profile image). Rotated 45° so it sits diagonally in the corner.
 *  - Emerald verification color, white text (kept legible even on mobile —
 *    NEVER collapses to a lone checkmark).
 *  - Subtle gold outline reinforces the premium aesthetic without stealing
 *    attention from the verification signal.
 *  - Kept intentionally small so it never overlaps the model's face on any
 *    card size (works down to ~120px card width).
 *  - VIP / TOP / paid-placement badges are rendered elsewhere (top-left).
 *    Do NOT stack other badges into the ribbon — the ribbon is reserved for
 *    identity verification only.
 *
 * Two sizes:
 *  - default: for compact directory cards
 *  - "lg":    for the profile-details hero image
 */
export default function VerifiedRibbon({ size = "default", "data-testid": testid = "verified-ribbon" }) {
  const isLarge = size === "lg";
  // Wrapper is a small square anchored to the top-right corner. Reduced by
  // ~50% vs. the previous ribbon so it never overlaps the face — even on
  // ultra-small thumbnails. Overflow-hidden clips the ribbon tails.
  const wrap = isLarge
    ? "w-[64px] h-[64px]"
    : "w-[46px] h-[46px] sm:w-[54px] sm:h-[54px]";
  // The rotated span is now compact: shorter width, tighter padding, smaller
  // type — designed to fit inside the reduced square without cropping.
  const ribbon = isLarge
    ? "top-[14px] right-[-24px] w-[100px] py-[3px] text-[9px] tracking-[0.14em]"
    : "top-[10px] right-[-22px] w-[80px] py-[2px] text-[7.5px] tracking-[0.12em] " +
      "sm:top-[12px] sm:right-[-24px] sm:w-[90px] sm:py-[2px] sm:text-[8.5px]";

  return (
    <div
      className={`pointer-events-none absolute top-0 right-0 overflow-hidden ${wrap}`}
      aria-hidden="false"
      data-testid={testid}
    >
      <span
        className={
          "absolute rotate-45 origin-center " +
          "flex items-center justify-center gap-1 " +
          "text-white font-semibold uppercase " +
          "shadow-[0_2px_6px_rgba(0,0,0,0.5)] " +
          // Emerald gradient stays legible on dark AND light imagery
          "bg-gradient-to-br from-emerald-500 to-emerald-600 " +
          // Subtle gold hairline
          "ring-1 ring-amber-300/70 " +
          "before:content-[''] before:absolute before:inset-0 before:pointer-events-none " +
          "before:border-y before:border-white/25 " +
          ribbon
        }
      >
        {/* Inline check + text — text NEVER hidden on mobile. */}
        <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        <span aria-label="Verified profile">Verified</span>
      </span>
    </div>
  );
}
